import { useState, useEffect, useMemo } from "react";
import { MdTrendingUp, MdFileDownload, MdPriorityHigh } from "react-icons/md";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
  Cell,
} from "recharts";

const exportToCsv = (filename, headers, rows) => {
  const encode = (val) => {
    if (val === null || val === undefined) return "";
    const str = String(val).replace(/"/g, '""');
    return `"${str}"`;
  };
  const allRows = [headers, ...rows];
  const csv = allRows.map((r) => r.map(encode).join(",")).join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 10,
        padding: "8px 13px",
        boxShadow: "0 4px 14px rgba(0,0,0,.1)",
        fontSize: "0.8rem",
      }}
    >
      {label && <p style={{ fontWeight: 700, marginBottom: 4 }}>{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill, fontWeight: 600 }}>
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

const roleLabel = (role) => {
  if (role === "super_admin") return "Super Admin";
  if (role === "admin") return "Admin";
  if (role === "accounts") return "Accounts";
  if (role === "hr") return "HR";
  return "Staff";
};

const TaskPriorityReport = () => {
  const { isAdmin } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [tRes, uRes] = await Promise.all([
          api.get("/tasks/admin/all", { params: { reportsView: 1 } }),
          api.get("/users/list"),
        ]);
        setTasks(tRes.data.data || []);
        setUsers(uRes.data.data || []);
      } finally {
        setLoading(false);
      }
    };
    if (isAdmin) load();
  }, [isAdmin]);

  const allUsers = useMemo(
    () => users.slice().sort((a, b) => a.name.localeCompare(b.name)),
    [users]
  );

  const filteredTasks = useMemo(() => {
    if (!selectedUserId) return tasks;
    return tasks.filter(
      (t) =>
        t.createdBy?._id === selectedUserId ||
        t.assignedTo?._id === selectedUserId
    );
  }, [tasks, selectedUserId]);

  const priorityMetrics = useMemo(() => {
    const critical = filteredTasks.filter((t) => t.priority === "critical")
      .length;
    const high = filteredTasks.filter((t) => t.priority === "high").length;
    const medium = filteredTasks.filter((t) => t.priority === "medium").length;
    const low = filteredTasks.filter((t) => t.priority === "low").length;
    return { critical, high, medium, low };
  }, [filteredTasks]);

  const chartData = useMemo(
    () => [
      {
        name: "Critical",
        value: priorityMetrics.critical,
        color: "#ef4444",
      },
      {
        name: "High",
        value: priorityMetrics.high,
        color: "#f97316",
      },
      {
        name: "Medium",
        value: priorityMetrics.medium,
        color: "#f59e0b",
      },
      {
        name: "Low",
        value: priorityMetrics.low,
        color: "#10b981",
      },
    ],
    [priorityMetrics]
  );

  if (!isAdmin) {
    return (
      <main className="page">
        <div className="card card-body">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <MdTrendingUp size={26} color="var(--primary)" />
            <div>
              <div style={{ fontWeight: 700, fontSize: "0.95rem" }}>
                Task priority report
              </div>
              <div
                style={{
                  fontSize: "0.8rem",
                  color: "var(--text-muted)",
                  marginTop: 4,
                }}
              >
                This report is available for admin users only.
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (loading) {
    return (
      <main className="page">
        <div className="loading">
          <div className="spinner" />
          <span className="loading-text">Loading task priority report...</span>
        </div>
      </main>
    );
  }

  return (
    <main className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <MdTrendingUp style={{ marginRight: 6 }} />
            Task Priority Report
          </h1>
          <p className="page-subtitle">
            Shows tasks based on priority levels to ensure important tasks are
            not delayed.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() =>
            exportToCsv(
              "task-priority-report.csv",
              [
                "User filter",
                "Critical",
                "High",
                "Medium",
                "Low",
              ],
              [
                [
                  selectedUserId
                    ? allUsers.find((u) => u._id === selectedUserId)?.name ||
                      "Selected user"
                    : "All users",
                  priorityMetrics.critical,
                  priorityMetrics.high,
                  priorityMetrics.medium,
                  priorityMetrics.low,
                ],
              ]
            )
          }
        >
          <MdFileDownload size={15} style={{ marginRight: 4 }} />
          Export Excel
        </button>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div
          className="card-body"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ minWidth: 220 }}>
            <label className="form-label">Select user</label>
            <select
              className="form-control"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
            >
              <option value="">All users</option>
              {allUsers.map((u) => (
                <option key={u._id} value={u._id}>
                  {u.name} ({roleLabel(u.role)})
                </option>
              ))}
            </select>
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              flex: 1,
              justifyContent: "flex-end",
            }}
          >
            <div className="stat-card" style={{ minWidth: 140 }}>
              <div
                style={{
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  color: "var(--text-muted)",
                  marginBottom: 4,
                }}
              >
                Critical priority tasks
              </div>
              <div className="stat-value">{priorityMetrics.critical}</div>
            </div>
            <div className="stat-card" style={{ minWidth: 140 }}>
              <div
                style={{
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  color: "var(--text-muted)",
                  marginBottom: 4,
                }}
              >
                High priority tasks
              </div>
              <div className="stat-value">{priorityMetrics.high}</div>
            </div>
            <div className="stat-card" style={{ minWidth: 140 }}>
              <div
                style={{
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  color: "var(--text-muted)",
                  marginBottom: 4,
                }}
              >
                Medium priority tasks
              </div>
              <div className="stat-value">{priorityMetrics.medium}</div>
            </div>
            <div className="stat-card" style={{ minWidth: 140 }}>
              <div
                style={{
                  fontSize: "0.7rem",
                  textTransform: "uppercase",
                  color: "var(--text-muted)",
                  marginBottom: 4,
                }}
              >
                Low priority tasks
              </div>
              <div className="stat-value">{priorityMetrics.low}</div>
            </div>
          </div>
        </div>
      </div>

      {filteredTasks.length === 0 ? (
        <div className="card card-body">
          <div className="empty-state">
            <MdPriorityHigh size={40} />
            <h3>No tasks found</h3>
            <p>There are no tasks matching the current filter.</p>
          </div>
        </div>
      ) : (
        <>
          <div className="card card-body" style={{ marginBottom: 20 }}>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={chartData}
                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="var(--border)"
                  vertical={false}
                />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip content={<ChartTip />} />
                <Legend wrapperStyle={{ fontSize: "0.72rem" }} />
                <Bar dataKey="value" name="Tasks">
                  {chartData.map((e, i) => (
                    <Cell key={i} fill={e.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="card">
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "1px solid var(--border)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontWeight: 700, fontSize: "0.9rem" }}>
                Real-time tasks {selectedUserId ? "for selected user" : ""}
              </span>
              <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                {filteredTasks.length} task{filteredTasks.length !== 1 ? "s" : ""}
              </span>
            </div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Priority</th>
                    <th>Status</th>
                    <th>Assigned To</th>
                    <th>Created By</th>
                    <th>Created</th>
                    <th>Deadline</th>
                  </tr>
                </thead>
                <tbody>
                  {[...filteredTasks]
                    .sort((a, b) => {
                      const order = { critical: 0, high: 1, medium: 2, low: 3 };
                      const pa = order[a.priority] ?? 4;
                      const pb = order[b.priority] ?? 4;
                      if (pa !== pb) return pa - pb;
                      return new Date(b.createdAt) - new Date(a.createdAt);
                    })
                    .map((t) => (
                      <tr key={t._id}>
                        <td style={{ fontWeight: 600 }}>{t.title}</td>
                        <td>
                          <span className={`badge badge-${t.priority || "medium"}`}>
                            {t.priority || "—"}
                          </span>
                        </td>
                        <td>
                          <span className={`badge badge-${t.status || "todo"}`}>
                            {t.status || "—"}
                          </span>
                        </td>
                        <td className="text-muted">
                          {t.assignedTo?.name ?? "—"}
                        </td>
                        <td className="text-muted">
                          {t.createdBy?.name ?? "—"}
                        </td>
                        <td className="text-muted">
                          {t.createdAt
                            ? new Date(t.createdAt).toLocaleString()
                            : "—"}
                        </td>
                        <td className="text-muted">
                          {t.deadline
                            ? new Date(t.deadline).toLocaleDateString()
                            : "—"}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </main>
  );
};

export default TaskPriorityReport;

